import { Component, computed, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { LabDip } from '../../../models/lab-dip/lab-dip';
import { Recipe } from '../../../models/recipe/recipe';
import { Shade } from '../../../models/shade/shade';
import { Business } from '../../../models/finance/business';
import { LabDipProxyService } from '../../../proxies/item/lab-dip.proxy.service';
import { RecipeProxyService } from '../../../proxies/item/recipe.proxy.service';
import { ShadeProxyService } from '../../../proxies/item/shade.proxy.service';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { ProcessSnapshot } from '../../../models/engineering/process';
import { AuthService } from '@card-software/nexus-utilities';
import { BreadcrumbService } from '@card-software/library';
import { TestLabDipAdminGroup } from '../../../models/authorization/enum/user-group.enum';

@Component({
  selector: 'app-recipe-container',
  templateUrl: './recipe-container.component.html',
  styleUrl: './recipe-container.component.scss',
})
export class RecipeContainerComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly labDipProxyService = inject(LabDipProxyService);
  private readonly recipeProxyService = inject(RecipeProxyService);
  private readonly shadeProxyService = inject(ShadeProxyService);
  private readonly businessProxyService = inject(BusinessProxyService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  canEdit = computed(() => {
    if (AuthService.hasRole(TestLabDipAdminGroup.TestLabDipAdmin)) {
      return true;
    }
    return false;
  });

  readonly isLoading = signal(0);
  readonly labDip = signal<LabDip | null>(null);
  readonly recipes = signal<Recipe[]>([]);
  readonly selectedRecipeId = signal<string | null>(null);
  readonly shadesMap = signal<Map<string, Shade>>(new Map());
  readonly businessMap = signal<Map<string, Business>>(new Map());
  activeTabIndex = 0;
  private currentLabDipId: string | null = null;

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      const labDipId = params.get('labDipId');
      const recipeId = params.get('recipeId');

      if (!labDipId) {
        this.router.navigate(['/lab-dips']);
        return;
      }

      this.currentLabDipId = labDipId;
      this.loadLabDip(labDipId);
      this.loadRecipes(labDipId, recipeId);
    });
  }

  private incrementLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private decrementLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private loadLabDip(labDipId: string): void {
    this.incrementLoading();
    this.labDipProxyService
      .getLabDipById(labDipId)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.labDip.set(response.data);
            this.breadcrumbService.setCurrent(response.data.labDipNumber);
          }
        },
        error: () => {
          this.router.navigate(['/lab-dips']);
        },
      });
  }

  private loadRecipes(labDipId: string, selectedRecipeId: string | null): void {
    this.incrementLoading();
    this.recipeProxyService
      .getRecipesByLabDipReference(labDipId)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (response.data && response.data.length > 0) {
            this.recipes.set(response.data);

            if (selectedRecipeId) {
              const index = response.data.findIndex((r) => r.id === selectedRecipeId);
              this.activeTabIndex = index >= 0 ? index : 0;
              this.selectedRecipeId.set(selectedRecipeId);
            } else {
              this.activeTabIndex = 0;
              this.selectedRecipeId.set(response.data[0].id || null);
              this.updateUrlWithRecipe(response.data[0].id);
            }

            // Load related data
            this.loadShades();
            this.loadBusinesses(response.data);
          } else {
            this.recipes.set([]);
          }
        },
      });
  }

  private loadShades(): void {
    this.incrementLoading();
    this.shadeProxyService
      .getAllShades()
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) {
            const map = new Map<string, Shade>();
            response.data.forEach((shade) => {
              if (shade.id) {
                map.set(shade.id, shade);
              }
            });
            this.shadesMap.set(map);
          }
        },
      });
  }

  private loadBusinesses(recipes: Recipe[]): void {
    const businessIds = new Set<string>();

    recipes.forEach((recipe) => {
      recipe.manufacturerLotNumberKeys?.forEach((mln) => {
        if (mln.manufacturerReference) {
          businessIds.add(mln.manufacturerReference);
        }
      });
    });

    if (businessIds.size > 0) {
      this.incrementLoading();
      this.businessProxyService
        .searchBatchIds(Array.from(businessIds))
        .pipe(finalize(() => this.decrementLoading()))
        .subscribe({
          next: (response) => {
            if (response.data) {
              const map = new Map<string, Business>();
              response.data.forEach((business) => {
                if (business.id) {
                  map.set(business.id, business);
                }
              });
              this.businessMap.set(map);
            }
          },
        });
    }
  }

  getShade(shadeRef: string): Shade | undefined {
    return this.shadesMap().get(shadeRef);
  }

  getBusiness(businessRef: string): Business | undefined {
    return this.businessMap().get(businessRef);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatLots(manufacturerLotNumberKeys: any[]): string {
    return manufacturerLotNumberKeys.map((mln) => mln.manufacturerLotNumber).join(', ');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onTabChange(event: any): void {
    const index = typeof event === 'number' ? event : event.index;

    if (index === this.recipes().length) {
      this.onCreateNewRecipe();
      return;
    }

    const selectedRecipe = this.recipes()[index];

    if (selectedRecipe?.id) {
      this.activeTabIndex = index;
      this.selectedRecipeId.set(selectedRecipe.id);
      this.updateUrlWithRecipe(selectedRecipe.id);
    }
  }

  private updateUrlWithRecipe(recipeId: string | undefined): void {
    if (recipeId) {
      const labDipId = this.labDip()?.id || this.currentLabDipId;

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { labDipId, recipeId },
        queryParamsHandling: 'merge',
      });
    }
  }

  onCreateNewRecipe(): void {
    const labDipId = this.labDip()?.id;
    if (labDipId) {
      this.router.navigate(['/recipe-details'], {
        queryParams: { labDipId, returnTo: 'recipe-container' },
      });
    }
  }

  editRecipe(recipeId: string): void {
    const labDipId = this.labDip()?.id;
    this.router.navigate(['/recipe-details'], {
      queryParams: { id: recipeId, labDipId, returnTo: 'recipe-container' },
    });
  }

  duplicateRecipe(recipeId: string): void {
    const labDipId = this.labDip()?.id;
    this.router.navigate(['/recipe-details'], {
      queryParams: { id: recipeId, labDipId, isCopy: true, returnTo: 'recipe-container' },
    });
  }

  normalizeProcessSnapshots(recipe: Recipe) {
    if (!recipe.processSnapshots) return;
    recipe.processSnapshots.forEach((process: ProcessSnapshot) => {
      if (Array.isArray(process.steps)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        process.stepIdsWithOrder = process.steps.map((stepObj: any) => {
          return {
            stepId: stepObj.stepSnapshot?.id || 0,
            order: stepObj.order,
            step: stepObj.stepSnapshot,
          };
        });
      } else {
        process.stepIdsWithOrder = [];
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/lab-dips']);
  }
}
